// shared-lib/dimensioning/datums.js
//
// Resolving a typed ref to the thing a dimension can measure to.
//
// Every dimension in this module is taken ALONG ONE AXIS, so a datum reduces
// to scalars on that axis: `lo` / `hi` (its extent) and `center`. That is the
// whole reason the rules stay short — "centred between", "centred on" and
// "offset from" are the same arithmetic once both ends are scalars.
//
// See claude-docs/2026-09-15-unified-dimensioning.md §3.

const { normalizeRef } = require('../group-customization/refs');
const { objectFootprint } = require('../geometry/objectFootprint');

// A wall counts as CROSSING the axis (a line to measure to) rather than
// running along it when its direction is this close to perpendicular.
const CROSSING_DOT = 0.2;

const dot2 = (p, u) => p[0] * u[0] + p[1] * u[1];
const dotXZ = (p, u) => p.x * u[0] + p.z * u[1];

/** Unit vector along a wall segment, or null for a degenerate one. */
function segmentDir(seg) {
  if (!seg?.start || !seg?.end) return null;
  const dx = seg.end[0] - seg.start[0];
  const dz = seg.end[1] - seg.start[1];
  const len = Math.hypot(dx, dz);
  if (!(len > 1e-9)) return null;
  return [dx / len, dz / len];
}

/** The perpendicular of a unit vector, in the plan's XZ plane. */
const perp = (u) => [-u[1], u[0]];

// A datum may name the ROOM wall or the STRUCTURE wall that backs it — a
// click in the plan can land on either, and they carry different ids.
function wallSegmentFor(ref, ctx) {
  const segs = ctx?.wallSegments || [];
  return (
    segs.find(
      (s) =>
        s.wallId === ref.id ||
        s.id === ref.id ||
        (s.structureWallId && s.structureWallId === ref.id)
    ) || null
  );
}

/**
 * A wall as scalars on `u`.
 *
 * A wall perpendicular to the axis is a LINE across it: every point projects
 * to the same scalar, so its extent on `u` is its THICKNESS, and `lo` / `hi`
 * are its two FACES. That distinction is the whole point — a drawing
 * dimensions to the face you could put a tape on, not to the centreline
 * buried inside the wall.
 *
 * A wall running along the axis is an extent of its LENGTH, and `at` picks an
 * end of it.
 */
function wallDatum(ref, ctx, u) {
  const seg = wallSegmentFor(ref, ctx);
  const dir = segmentDir(seg);
  if (!dir) return null;

  const a = dot2(seg.start, u);
  const b = dot2(seg.end, u);

  if (Math.abs(dot2(dir, u)) < CROSSING_DOT) {
    // A room wall is a zero-thickness PLANE; the thing with faces is the
    // structure wall behind it. Read the centreline and thickness from that
    // solid when we have it, so the face this resolves to is one you could
    // put a tape on rather than a plane that may sit anywhere across the
    // wall's depth — including dead centre.
    const plane = (a + b) / 2; // both ends project to the same scalar
    const solid = seg.solid;
    const thickness = solid ? solid.thickness : seg.thickness || 0;
    const s = solid ? dot2([solid.center.x, solid.center.z], u) : plane;
    const half = Math.max(0, thickness) / 2;
    return {
      lo: s - half,
      hi: s + half,
      center: s,
      crossing: true,
      isWall: true,
      dir,
      seg,
    };
  }

  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  const at = ref.at;
  const center = at === 'start' ? a : at === 'end' ? b : (lo + hi) / 2;
  return { lo, hi, center, crossing: false, isWall: true, dir, seg };
}

/** Anything with a placed footprint: an object, and an opening drawn like one. */
function footprintDatum(entity, at, u) {
  const corners = entity ? objectFootprint(entity) : null;
  if (!corners || corners.length === 0) return null;
  const scalars = corners.map((c) => dotXZ(c, u));
  const lo = Math.min(...scalars);
  const hi = Math.max(...scalars);
  const center = at === 'start' ? lo : at === 'end' ? hi : (lo + hi) / 2;
  return { lo, hi, center, crossing: false, dir: null, corners };
}

/**
 * Resolve a ref to `{ lo, hi, center }` on the axis `u`. PURE.
 *
 * Returns null rather than guessing — an unresolvable datum must produce no
 * dimension at all, never one measured to the wrong place.
 *
 * @param {Object|string} ref  a `refs.js` endpoint ref.
 * @param {{wallSegments?:Array, entitiesById?:Map|Object}} ctx
 * @param {[number,number]} u  unit axis in the XZ plane.
 */
function resolveDatum(ref, ctx, u) {
  const r = normalizeRef(ref);
  if (!r || !u) return null;
  if (r.kind === 'wall') return wallDatum(r, ctx, u);
  if (r.kind === 'object' || r.kind === 'opening') {
    return footprintDatum(lookupEntity(r.id, ctx), r.at || r.edge, u);
  }
  return null;
}

function lookupEntity(id, ctx) {
  const byId = ctx?.entitiesById;
  if (!byId) return null;
  return typeof byId.get === 'function' ? byId.get(id) : byId[id];
}

/**
 * The axis a dimension is taken along, as a unit vector. PURE.
 *
 * An explicit `axis` on the record always wins. Otherwise it is read from the
 * datum AND the caller's `mode`, because the datum alone does not settle it:
 * "centred on this wall" runs ALONG the wall, "offset from this wall" runs
 * ACROSS it, and both name the same datum. With neither an explicit axis nor
 * a datum that carries a direction, there is no axis and no dimension — never
 * a default.
 *
 * @param {'along'|'across'} mode  what the RULE is measuring.
 */
function resolveAxis(record, ctx, mode = 'along') {
  if (record?.axis === 'x') return [1, 0];
  if (record?.axis === 'z') return [0, 1];

  const refs = (
    Array.isArray(record?.datum) ? record.datum : [record?.datum]
  ).filter(Boolean);
  const first = refs[0] ? normalizeRef(refs[0]) : null;
  if (!first) return null;

  const orient = (dir) => (mode === 'across' ? perp(dir) : dir);

  if (first.kind === 'wall') {
    const dir = segmentDir(wallSegmentFor(first, ctx));
    return dir ? orient(dir) : null;
  }

  // Any PLACED thing carries its own direction: an opening's width runs along
  // its wall, and so does a fireplace's, a vanity's, a range's. Same formula,
  // same convention as `rotation.y = atan2(normal.x, normal.z)` — which is why
  // this is one branch and not one per kind.
  if (first.kind === 'object' || first.kind === 'opening') {
    const ry = lookupEntity(first.id, ctx)?.rotation?.y;
    if (typeof ry === 'number') return orient([Math.cos(ry), -Math.sin(ry)]);
  }

  return null;
}

/**
 * The scalar a dimension should END at on this datum, seen from `s0`.
 *
 * A WALL is measured to its FACE — the surface you could put a tape on —
 * because that is what a drawing dimensions to and what a builder can find.
 * Anything else is measured to its centreline: a fixture's centre is the
 * meaningful thing about it, not its edge.
 */
function datumEdgeToward(d, s0) {
  if (!d || !d.isWall) return d ? d.center : null;
  return d.center < s0 ? d.hi : d.lo;
}

/**
 * Which way each rule measures — the ONE answer, because it was written out
 * in both the resolver and the apply step and drifted: `offset-from` runs
 * ACROSS its datum, but the mover read it as ALONG, so typing a distance
 * moved the object down the wall instead of away from it.
 *
 * "Centred on this wall" runs along the wall; "offset from this wall" and
 * "centred between these walls" run across them.
 */
const AXIS_MODE = {
  'centered-between': 'across',
  'centered-on': 'along',
  'offset-from': 'across',
};

const axisModeFor = (rule) => AXIS_MODE[rule] || 'along';

/**
 * Where a dimension's extension line should REACH BACK to on its datum. PURE.
 *
 * A dimension is taken at the SUBJECT's cross-axis position, and the thing it
 * measures to rarely sits on that same line — a wall bounding a room that
 * opens to the next one stops short, and a fireplace or a window is simply
 * somewhere else along the wall. The endpoint then hangs where the datum
 * WOULD be if it continued, and the dotted line points at nothing.
 *
 * The anchor keeps the measured coordinate on the axis — moving it there
 * would draw a slant, not an extension — and travels only ACROSS, clamped to
 * the datum's own extent.
 *
 * @param {Object} d      a datum from `resolveDatum`.
 * @param {number} faceS  the scalar the dimension measured to.
 * @param {[number,number]} point  the dimension's endpoint.
 * @param {[number,number]} u      the measuring axis.
 * @returns {[number,number]|null} null when the endpoint already lies on the
 *   datum, so nothing needs extending.
 */
function datumExtensionAnchor(d, faceS, point, u) {
  if (!d || !point || !u) return null;
  const v = perp(u);

  // The datum's extent on the CROSS axis: a wall's run, or an object's
  // footprint.
  let scalars = null;
  if (d.seg?.start && d.seg?.end) {
    scalars = [dot2(d.seg.start, v), dot2(d.seg.end, v)];
  } else if (Array.isArray(d.corners) && d.corners.length) {
    scalars = d.corners.map((c) => dotXZ(c, v));
  }
  if (!scalars) return null;

  const lo = Math.min(...scalars);
  const hi = Math.max(...scalars);
  const pv = dot2(point, v);
  if (pv >= lo && pv <= hi) return null; // already on it — nothing to extend

  const cv = Math.min(hi, Math.max(lo, pv));
  return [u[0] * faceS + v[0] * cv, u[1] * faceS + v[1] * cv];
}

module.exports = {
  resolveDatum,
  datumEdgeToward,
  axisModeFor,
  datumExtensionAnchor,
  resolveAxis,
  segmentDir,
  perp,
  dot2,
  dotXZ,
};
